/**
 * sis-lpo-preview: two repairs to LPO's live preview in the gallery.
 *
 * 1. KEEP IT MOUNTED (below).
 * 2. INSET PLAIN DESIGNS (owner 2026-10-12): a design drawn on a plain
 *    background fills LPO's canvas edge to edge, so Horizon's gallery arrows
 *    sat on the design. What LPO draws changes over time: on a sign like
 *    Texas it shows a ROOM PHOTO until the customer types, then the design on
 *    a transparent canvas over its own white box. So the canvas CORNERS are
 *    checked RIGHT AFTER EACH LPO DRAW: the drawing calls on LPO's own canvas
 *    context queue one microtask, which runs after LPO's synchronous render
 *    and before the browser paints, so the 100% -> 80% switch never shows as
 *    a flick (owner 2026-10-12; a 0.7 s poll did). An empty canvas (LPO
 *    still loading) decides nothing. Plain (transparent, or one light colour)
 *    -> only the canvas scales to 80% (about the 3D view's framing); LPO's
 *    box stays full size, filled with the canvas's own background colour. A
 *    photo keeps its full size.
 *
 * THE BUG (LPO's own code, measured 2026-10-12 on the staging theme): on a
 * Horizon theme LPO re-inits its preview 100 ms after every variant update
 * (size or colour). Its _findOrCreatePreviewContainer() finds the first
 * gallery slide - the slide that already holds LPO's preview - and EMPTIES it,
 * removing LPO's own preview wrapper; the caller then re-appends the wrapper
 * only `if (wrapper.parentNode)`, which is now null. Result: a blank first
 * slide for the rest of the visit, and the live 3D preview (which reads LPO's
 * canvas) stops updating. Worth reporting to Cloudlift; this is the theme-side
 * repair until they fix it.
 *
 * THE REPAIR: remember LPO's wrapper and the slide it lives in. When the wrapper
 * leaves the page and LPO has not put it (or a new one) back after a short
 * grace, append the SAME node to that slide again (only that slide: if it is
 * gone too, LPO rebuilt its gallery and nothing is touched). The node keeps its canvas and LPO keeps drawing into it, so nothing is
 * re-created. Loaded only where LPO is allowed (snippets/sis-lpo-guard.liquid);
 * remove with LPO.
 */
(() => {
  'use strict';

  if (window.__sisLpoKeeper) return;
  window.__sisLpoKeeper = true;

  const WRAPPER = '.cl-po--preview-wrapper';
  const GRACE_MS = 400; // LPO re-inits 100 ms after the variant update

  /** @type {HTMLElement | null} */ let wrapper = null;
  /** @type {Element | null} */ let home = null;
  let timer = 0;
  /** @type {number[]} */ let insetTimers = [];
  let lastBg = '';

  // Is the canvas background plain? Judged from its four CORNERS (4 x 4 px
  // each of a 32 px copy): a design may touch the edges (the Texas ring, the
  // Tree of Life circle) but leaves the corners as background, while a room
  // photo's corners differ. Returns 'blank' (nothing drawn yet: decide
  // nothing), 'transparent', a light colour, or null (a photo, a dark
  // background, or unreadable: leave it at full size).
  function plainBackground(canvas) {
    let px;
    try {
      const t = document.createElement('canvas');
      t.width = t.height = 32;
      const x = t.getContext('2d', { willReadFrequently: true });
      x.drawImage(canvas, 0, 0, 32, 32);
      px = x.getImageData(0, 0, 32, 32).data;
    } catch (e) {
      return null;
    }
    let drawn = false;
    for (let o = 3; o < px.length; o += 4) {
      if (px[o] >= 16) {
        drawn = true;
        break;
      }
    }
    if (!drawn) return 'blank';
    let total = 0;
    let clear = 0;
    /** @type {Map<string, {n: number, r: number, g: number, b: number}>} */
    const buckets = new Map();
    for (const [x0, y0] of [[0, 0], [28, 0], [0, 28], [28, 28]]) {
      for (let j = y0; j < y0 + 4; j++) {
        for (let i = x0; i < x0 + 4; i++) {
          const o = (j * 32 + i) * 4;
          total++;
          if (px[o + 3] < 16) {
            clear++;
            continue;
          }
          const key = `${px[o] >> 4},${px[o + 1] >> 4},${px[o + 2] >> 4}`;
          const bucket = buckets.get(key) || { n: 0, r: 0, g: 0, b: 0 };
          bucket.n++;
          bucket.r += px[o];
          bucket.g += px[o + 1];
          bucket.b += px[o + 2];
          buckets.set(key, bucket);
        }
      }
    }
    if (clear >= total * 0.6) return 'transparent';
    let top = null;
    for (const bucket of buckets.values()) if (!top || bucket.n > top.n) top = bucket;
    if (!top || top.n < total * 0.85) return null;
    const [r, g, b] = [top.r / top.n, top.g / top.n, top.b / top.n].map(Math.round);
    // a dark "background" is more likely the design reaching the corners
    if (0.299 * r + 0.587 * g + 0.114 * b < 200) return null;
    return `rgb(${r}, ${g}, ${b})`;
  }

  // `drawn`: the canvas LPO just drew on. Its wrapper is found from the canvas,
  // not from memory: LPO may wrap the same canvas in a new wrapper when the
  // customer starts personalizing, and a stale reference cost ~0.25 s.
  function inset(drawn) {
    const canvas = drawn || (wrapper && wrapper.querySelector('canvas'));
    if (!canvas || !canvas.width || !canvas.isConnected) return;
    const owner = /** @type {HTMLElement | null} */ (canvas.closest(WRAPPER));
    if (!owner) return;
    if (owner !== wrapper) {
      wrapper = owner;
      home = owner.parentElement;
    }
    hook(canvas);
    const found = plainBackground(canvas);
    if (found === 'blank') return; // LPO has not drawn yet
    const bg = found || '';
    if (bg === lastBg && wrapper.classList.contains('sis-lpo-inset') === !!bg) return;
    lastBg = bg;
    wrapper.classList.toggle('sis-lpo-inset', !!bg);
    // fill LPO's box with the canvas's own background, so the inset canvas
    // shows no edge (an off-white canvas on the white box did)
    const fill = !!bg && bg !== 'transparent';
    wrapper.classList.toggle('sis-lpo-fill', fill);
    if (fill) wrapper.style.setProperty('--sis-lpo-bg', bg);
  }

  // Check once right after LPO draws: its drawing calls queue one microtask,
  // which runs when LPO's synchronous render is done, before the paint.
  /** @type {Set<HTMLCanvasElement>} */ const pending = new Set();
  function queueCheck(canvas) {
    if (pending.has(canvas)) return;
    pending.add(canvas);
    queueMicrotask(() => {
      pending.delete(canvas);
      inset(canvas);
    });
  }
  /** @type {WeakSet<object>} */ const hooked = new WeakSet();
  function hook(canvas) {
    // Fabric adds `lower-canvas` once it owns the canvas and its 2D context,
    // so getContext here never creates a context LPO did not ask for.
    if (!canvas.classList.contains('lower-canvas')) return;
    const ctx = canvas.getContext('2d');
    if (!ctx || hooked.has(ctx)) return;
    hooked.add(ctx);
    for (const name of ['clearRect', 'fillRect', 'drawImage', 'putImageData', 'fillText']) {
      const draw = ctx[name];
      if (typeof draw !== 'function') continue;
      ctx[name] = function (...args) {
        queueCheck(canvas);
        return draw.apply(this, args);
      };
    }
  }

  // After a (re)mount, and a slow safety net for a draw the hook misses.
  function scheduleInset() {
    insetTimers.forEach(clearTimeout);
    insetTimers = [0, 300].map((ms) => window.setTimeout(() => inset(), ms));
  }
  window.setInterval(() => {
    if (!document.hidden) inset();
  }, 1500);

  const findWrapper = () =>
    document.querySelector(`.product-information ${WRAPPER}`) || document.querySelector(WRAPPER);

  function remember() {
    const found = /** @type {HTMLElement | null} */ (findWrapper());
    if (found) {
      const isNew = found !== wrapper;
      wrapper = found;
      home = found.parentElement;
      if (isNew) {
        inset(); // now: hooks LPO's canvas before its next draw is painted
        scheduleInset();
      }
    }
  }

  function repair() {
    timer = 0;
    if (!wrapper) return;
    if (wrapper.isConnected) {
      remember(); // LPO put it back itself (e.g. into a replaced gallery)
      return;
    }
    const fresh = findWrapper();
    if (fresh) {
      remember(); // LPO built a new preview: follow that one
      return;
    }
    // Only the slide LPO itself emptied. If that slide is gone too, LPO
    // rebuilt its gallery or dropped the preview on purpose: leave it, never
    // clear someone else's slide.
    if (!home || !home.isConnected) return;
    home.appendChild(wrapper);
    scheduleInset();
  }

  // childList only; the repair's own append adds nodes, never removes the
  // wrapper, so it cannot retrigger itself.
  new MutationObserver((records) => {
    if (!wrapper) {
      remember();
      return;
    }
    for (const record of records) {
      for (const node of record.removedNodes) {
        if (node === wrapper || (node.nodeType === 1 && /** @type {Element} */ (node).contains(wrapper))) {
          clearTimeout(timer);
          timer = window.setTimeout(repair, GRACE_MS);
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  remember();
})();
